import { useState } from 'react';
import {
  APP_NAME,
  PROFILE_TYPES,
  PROFILE_TYPE_HINT,
  PROFILE_TYPE_LABEL,
  type ProfileType,
} from 'shared';
import { api, type ApiError } from '../api';
import Icon, { type IconName } from '../Icon';
import { alert, haptic, notify } from '../telegram';

const ICON: Record<ProfileType, IconName> = {
  CARRIER: 'truck',
  FORWARDER: 'route',
  SHIPPER: 'box',
};

/**
 * Приветствие и выбор профиля деятельности.
 *
 * Показывается один раз — пока профиль не выбран. Спрашиваем сразу, потому
 * что от ответа зависит, что человек увидит на первом же экране: перевозчику
 * нужна лента грузов, грузовладельцу — форма заявки.
 */
export default function Welcome({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState<ProfileType | null>(null);

  async function choose(type: ProfileType) {
    haptic();
    setBusy(type);
    try {
      await api.setProfileType(type);
      notify('success');
      onDone();
    } catch (e) {
      /*
       * 409 — профиль уже выбран.
       *
       * Так бывает, когда первый запрос прошёл, а перезагрузка не случилась:
       * человек жмёт второй раз и получает «ошибку» на действие, которое
       * на самом деле удалось. Проще молча увести дальше.
       */
      if ((e as ApiError).status === 409) {
        onDone();
        return;
      }

      await alert((e as Error).message);
      setBusy(null);
    }
  }

  return (
    <div className="welcome">
      <div className="welcome__logo" aria-hidden>
        <Icon name="truck" size={30} />
      </div>

      <h2>Добро пожаловать в {APP_NAME}</h2>
      <p className="meta" style={{ marginBottom: 24 }}>
        Укажите профиль деятельности. Его увидят другие участники — так проще
        находить партнёров. Позже изменить профиль можно через поддержку.
      </p>

      {PROFILE_TYPES.map((t) => (
        <button
          key={t}
          className="role-card"
          disabled={busy !== null}
          onClick={() => void choose(t)}
        >
          <span className="role-card__icon">
            <Icon name={ICON[t]} size={20} />
          </span>
          <span className="role-card__text">
            <strong>{PROFILE_TYPE_LABEL[t]}</strong>
            <span className="meta">{PROFILE_TYPE_HINT[t]}</span>
          </span>
          <Icon name="arrow" size={18} />
        </button>
      ))}

      <p className="meta" style={{ marginTop: 16 }}>
        Выбор влияет только на то, какие разделы вы видите. Ставки, документы
        и рейсы работают одинаково для всех.
      </p>
    </div>
  );
}
